/**
 * Zod schemas for the Gong Flow Play mirror table (`gong_flows`, U6 — Gong
 * Flow Play + Queue redesign). Imported by BOTH the sync job that upserts
 * `gong_flows.steps` (U8) and the browse/step-editor API that reads it (U9)
 * — house rule for wire/DB shapes shared by two sides of a boundary.
 */

import { z } from 'zod';

/**
 * Gong Engage flow step action types, confirmed live against a 6-step test
 * flow (one per type) — see `docs/flow/gong-flow-play-spec-handoff.md` Check
 * 3 (CONFIRMED 2026-08-18). `AUTO_SEND_EMAIL` is distinct from `SEND_EMAIL`
 * — automation-flow detection (a Q3 deny-list signal) reads off step
 * composition, so collapsing the two would lose that signal.
 */
export const GongFlowStepActionTypeSchema = z.enum([
  'SEND_EMAIL',
  'AUTO_SEND_EMAIL',
  'CALL',
  'LINKEDIN_CONNECT',
  'LINKEDIN_MESSAGE',
  'CUSTOM',
]);
export type GongFlowStepActionType = z.infer<typeof GongFlowStepActionTypeSchema>;

/**
 * One step of a synced Gong flow, as returned by `POST /v2/flows/steps`.
 * `subject`/`body` are the flow's DEFAULT copy for the step (body is HTML,
 * including non-email step types where body doubles as the call/LinkedIn
 * script) — a rep's per-enrollment override lives in `enrollment_item_step`,
 * not here. `stepOrder` is 1-based, matching Gong's `overrides.steps[].number`.
 */
export const GongFlowStepSchema = z.object({
  stepOrder: z.number().int().positive(),
  actionType: GongFlowStepActionTypeSchema,
  subject: z.string(),
  body: z.string(),
  /**
   * Gong's own "this step replies in the thread of the previous one" flag.
   * Gong REJECTS an override that sets a subject on a reply step
   * (`Step subject cannot be set for reply steps`) and 400s the WHOLE
   * enrollment, not just that step — so the send path must know which steps
   * are replies before it builds `overrides.steps` (see
   * `mini-apps/playbooks/send-payload.ts`).
   *
   * `.default(false)` is load-bearing, NOT laziness: this schema is read back
   * over ~729 rows that were mirrored before the column existed, via
   * `parseDbJson`, which logs-and-falls-back rather than throwing. A REQUIRED
   * field would make every one of those rows fail to parse until the next
   * sync backfilled it. `false` is also the safe default — it means "send the
   * subject", which is the pre-existing behaviour.
   */
  isReply: z.boolean().default(false),
  /**
   * Gong's delay (calendar days) between the previous step and this one,
   * carried from `GongFlowStepDetail.daysFromPreviousStep`. The Gong
   * reconcile's decide() uses it to tell whether a prospect that completed
   * step N is merely waiting on step N+1's delay (mid-step) or genuinely done.
   *
   * `.optional()`, NEVER `.default()`: rows mirrored before this field existed
   * have no delay, and a default would invent one. Absent means "unknown" and
   * decide() treats it as mid-step.
   */
  daysFromPreviousStep: z.number().optional(),
});
export type GongFlowStep = z.infer<typeof GongFlowStepSchema>;

/** The full `gong_flows.steps` JSON column shape — an ordered step list. */
export const GongFlowStepsSchema = z.array(GongFlowStepSchema);
export type GongFlowSteps = z.infer<typeof GongFlowStepsSchema>;

/**
 * Sentinel value `browseGongFlows` (`src/services/flow/gong-flow-catalog.ts`)
 * writes into `gong_flows.visibility` when a previously-mirrored flow is
 * absent from a later successful full `GET /v2/flows` read. `visibility`
 * otherwise carries Gong's own value (`ALL`/`OWNER`/… — see
 * `GongFlowListItemSchema`), so this sentinel is a Terra-owned OVERLAY on
 * that column, never a value Gong itself returns. Chosen over a separate
 * boolean column because the mirror table doc comment (`core.prisma`
 * `GongFlow` model) already commits to "visibility set to an unavailable
 * value" as the flagging mechanism — a flow that vanishes from the API is
 * flagged this way, NEVER hard-deleted (enrollments and send history still
 * resolve display names via `flowId`). (The daily DBOS sync workflow that
 * originally owned this write was deleted — PR #1312 R6 — before it ever
 * completed a successful run; `browseGongFlows` is now the sole writer.)
 *
 * Both `browseGongFlows` (write) and the exposure deriver / send-time guard
 * (`src/services/flow/play-derivation.ts`, read) import this single constant
 * so the sentinel can never drift between the two sides.
 */
export const GONG_FLOW_UNAVAILABLE_VISIBILITY = 'UNAVAILABLE';

/** True when a `gong_flows` row is still live-exposable (not flagged absent). */
export function isGongFlowAvailable(visibility: string): boolean {
  return visibility !== GONG_FLOW_UNAVAILABLE_VISIBILITY;
}

/**
 * `flow_play_types.slug` / `action_types.slug` for the Gong Flow play
 * (`prisma/schema/migrations/20260819000000_gong_flow_play_tables/migration.sql`
 * seeds the `action_types` row; the `flow_play_types`/`action_definitions`
 * connector row lands in U10). Shared so U9's suggested-3 ranking (looks up
 * the play type to scope its `ActionExecution` groupBy) and U10's connector
 * registration never drift on the literal.
 */
export const GONG_FLOW_PLAY_TYPE_SLUG = 'gong_flow';
